import { useState } from 'react'
import { useQuery } from 'convex/react'
import { Link } from 'react-router-dom'
import { Building2, ChevronRight, Plus, Search } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import { usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'

export default function OrganizationList() {
  const { hasPermission } = usePermissions()
  const canManage = hasPermission('manage_companies')
  const canView = canManage || hasPermission('view_companies')
  const organizations = useQuery(
    api.organizations.listOrganizations,
    canView ? {} : 'skip'
  )
  const [search, setSearch] = useState('')

  if (!canView) {
    return (
      <Card className="shadow-none">
        <CardContent className="p-8 text-center">
          Non hai i permessi per consultare le società.
        </CardContent>
      </Card>
    )
  }
  if (organizations === undefined) return <Loading />

  const query = search.trim().toLocaleLowerCase('it')
  const filtered = organizations.filter((organization) =>
    [
      organization.nome,
      organization.partita_iva,
      organization.codice_fiscale,
    ].some((value) => value?.toLocaleLowerCase('it').includes(query))
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-[-0.02em] text-gray-950">
              Società
            </h1>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
              Gestisci le società, le cliniche collegate e la condivisione dei
              listini.
            </p>
          </div>
        </div>
        {canManage && (
          <Button asChild className="w-full sm:w-auto">
            <Link to="/organizations/new">
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Nuova società
            </Link>
          </Button>
        )}
      </div>

      <Card className="overflow-hidden shadow-none">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full sm:max-w-xl">
            <label
              htmlFor="organization-search"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              Cerca società
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                aria-hidden="true"
              />
              <Input
                id="organization-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nome, partita IVA o codice fiscale"
                className="pl-10"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600" aria-live="polite">
            <span className="font-semibold tabular-nums">
              {filtered.length}
            </span>{' '}
            società
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <Building2
              className="mx-auto h-10 w-10 text-gray-400"
              aria-hidden="true"
            />
            <h2 className="mt-3 text-lg font-semibold text-gray-950">
              {query ? 'Nessuna società trovata' : 'Non ci sono ancora società'}
            </h2>
            <p className="mx-auto mt-1 max-w-lg text-sm leading-6 text-gray-600">
              {query
                ? 'Prova con un altro nome o identificativo fiscale.'
                : 'Crea una società e collega le sue cliniche. Le cliniche esistenti restano disponibili nella sezione Cliniche.'}
            </p>
            {!query && canManage && (
              <Button asChild className="mt-4">
                <Link to="/organizations/new">Crea società</Link>
              </Button>
            )}
          </div>
        ) : (
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">Società e cliniche collegate</caption>
            <thead className="hidden bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-600 md:table-header-group">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Società
                </th>
                <th scope="col" className="px-4 py-3">
                  Cliniche
                </th>
                <th scope="col" className="px-4 py-3">
                  Listini di società
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Dettagli
                </th>
              </tr>
            </thead>
            <tbody className="block divide-y divide-gray-200 md:table-row-group">
              {filtered.map((organization) => (
                <tr
                  key={organization._id}
                  className="block bg-white hover:bg-gray-50/70 md:table-row"
                >
                  <th
                    scope="row"
                    className="block px-4 pb-2 pt-4 text-left font-normal md:table-cell md:py-4"
                  >
                    <Link
                      to={`/organizations/${organization._id}/edit`}
                      className="break-words font-semibold text-gray-950 underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      {organization.nome}
                    </Link>
                    {(organization.partita_iva ||
                      organization.codice_fiscale) && (
                      <p className="mt-1 break-all text-sm text-gray-600">
                        {organization.partita_iva
                          ? `P. IVA ${organization.partita_iva}`
                          : `C.F. ${organization.codice_fiscale}`}
                      </p>
                    )}
                  </th>
                  <td className="block px-4 py-2 text-sm text-gray-700 md:table-cell md:py-4">
                    <span className="font-medium tabular-nums">
                      {organization.clinic_count}
                    </span>
                    <span className="ml-1 md:sr-only">cliniche</span>
                  </td>
                  <td className="block px-4 py-2 md:table-cell md:py-4">
                    <Badge variant="outline">
                      {organization.share_price_lists
                        ? 'Condivisione attiva'
                        : 'Condivisione disattivata'}
                    </Badge>
                  </td>
                  <td className="block px-4 pb-4 pt-2 md:table-cell md:py-4 md:text-right">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="min-h-10"
                    >
                      <Link
                        to={`/organizations/${organization._id}/edit`}
                        aria-label={`Apri società ${organization.nome}`}
                      >
                        Apri
                        <ChevronRight
                          className="ml-1 h-4 w-4"
                          aria-hidden="true"
                        />
                      </Link>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}
